import {
  clone,
  cloneDeep,
  each,
  intersection,
  remove,
  difference,
  get,
  set,
  filter,
  flatten,
  keys,
  isNumber,
  isEqual,
  isInteger,
  isString,
} from 'lodash'
import {TransitionProps} from 'react-transition-group/Transition'
import {
  CreditCardPaymentMethods,
  getCreditCardType,
  getLocationParams,
  isCreditCardPaymentMethod,
  isPostFinancePaymentMethod,
  PaymentCurrency,
  PaymentFlow,
  PaymentMethod,
  PostFinancePaymentMethods,
  Router,
} from '@raisenow/epik'
import {ValidationErrors} from './Validator'
import {WidgetConfig} from './WidgetConfig'
import {IFormBase} from './forms/IFormBase'
import {PaymentForm} from './forms/PaymentForm'
import {SubscriptionRenewForm} from './forms/SubscriptionRenewForm'
import {WidgetLocation} from './WidgetApi'

///////////////////////////////////////////////////////////////////////////////

type SlideTransitionProps = {
  timeout: number
} & Partial<TransitionProps>

export type OptionItem = {
  value: string | number
  label?: string
}

///////////////////////////////////////////////////////////////////////////////

export function findDefinedValue(...values: any[]): any {
  return values.find((v) => v !== null && v !== undefined)
}

///////////////////////////////////////////////////////////////////////////////

export function filterOutRedundantPostFinancePaymentMethods(
  availablePaymentMethods: PaymentMethod[],
): PaymentMethod[] {
  let paymentMethods = clone(availablePaymentMethods)
  let availablePostFinancePaymentMethods = intersection(
    availablePaymentMethods,
    PostFinancePaymentMethods,
  )
  let availablePostFinancePaymentMethodsLength =
    availablePostFinancePaymentMethods.length

  remove(paymentMethods, (el) => {
    if (availablePostFinancePaymentMethodsLength > 1) {
      return (
        isPostFinancePaymentMethod(el) && el !== PaymentMethod.POST_FINANCE_CARD
      )
    }
    return false
  })

  return paymentMethods
}

export function filterOutSpecificCreditCardPaymentMethods(
  paymentMethods: PaymentMethod[],
): PaymentMethod[] {
  let specificCreditCardPaymentMethods = clone(CreditCardPaymentMethods).filter(
    (pm) => pm !== PaymentMethod.CREDIT_CARD,
  )

  return difference(paymentMethods, specificCreditCardPaymentMethods)
}

export function getPaymentMethodTitle(
  paymentMethod: PaymentMethod,
  form: PaymentForm | SubscriptionRenewForm,
  trans: any,
): string {
  let paymentMethodTitle = trans(`payment_methods.${paymentMethod}`)

  if (isCreditCardPaymentMethod(paymentMethod)) {
    paymentMethodTitle = trans(`payment_methods.${PaymentMethod.CREDIT_CARD}`)
  }

  if (isPostFinancePaymentMethod(paymentMethod)) {
    let availablePostFinancePaymentMethods = filter(
      form.getPossiblePaymentMethods(),
      (v) => isPostFinancePaymentMethod(v),
    )

    if (availablePostFinancePaymentMethods.length > 1) {
      paymentMethodTitle = trans('payment_methods.pfc_pef')
    }
  }

  return paymentMethodTitle
}

///////////////////////////////////////////////////////////////////////////////

export function getElementDomPath(el) {
  if (!el) {
    return
  }

  var stack = []

  while (el.parentNode !== null) {
    let name = el.nodeName.toLowerCase()
    let index = ''

    let sibCount = 0
    let sibIndex = 0

    for (let i = 0; i < el.parentNode.childNodes.length; i++) {
      let sib = el.parentNode.childNodes[i]

      if (sib.nodeName === el.nodeName) {
        if (sib === el) {
          sibIndex = sibCount
        }
        sibCount++
      }
    }

    if (sibCount > 1) {
      index = ':nth-of-type(' + (sibIndex + 1) + ')'
    }

    stack.unshift(name + index)

    el = el.parentNode
  }

  stack.splice(0, 1)

  return stack.join(' > ')
}

///////////////////////////////////////////////////////////////////////////////

export const buildSlideTransitionProps = (
  options: SlideTransitionProps,
): SlideTransitionProps => {
  let elementHeight
  let {timeout} = options
  timeout = isNumber(timeout) ? timeout : 300
  const transition = `all ${timeout}ms ease-out`
  const forceReflow = (node) => node.scrollTop

  const onEnter = (node: HTMLElement, isAppearing: boolean) => {
    elementHeight = node.offsetHeight + 'px'

    addStyles(node, {
      overflow: 'hidden',
      height: 0,
      opacity: 0,
    })

    if (options.onEnter) {
      options.onEnter(node, isAppearing)
    }
  }

  const onEntering = (node: HTMLElement, isAppearing: boolean) => {
    forceReflow(node)

    addStyles(node, {
      transition: transition,
      height: elementHeight,
      opacity: 1,
    })

    if (options.onEntering) {
      options.onEntering(node, isAppearing)
    }
  }

  const onEntered = (node: HTMLElement, isAppearing: boolean) => {
    removeStyles(node, ['transition', 'overflow', 'height', 'opacity'])

    if (options.onEntered) {
      options.onEntered(node, isAppearing)
    }
  }

  const onExit = (node: HTMLElement) => {
    addStyles(node, {
      overflow: 'hidden',
      height: node.offsetHeight + 'px',
      opacity: 1,
    })

    if (options.onExit) {
      options.onExit(node)
    }
  }

  const onExiting = (node: HTMLElement) => {
    forceReflow(node)

    addStyles(node, {
      transition: transition,
      height: 0,
      opacity: 0,
    })

    if (options.onExiting) {
      options.onExiting(node)
    }
  }

  const onExited = (node: HTMLElement) => {
    removeStyles(node, ['transition', 'overflow', 'height', 'opacity'])

    if (options.onExited) {
      options.onExited(node)
    }
  }

  return {
    timeout,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
  }
}

export const addStyles = (node: HTMLElement, styles: any) => {
  each(styles, (v, k) => {
    node.style[k] = v
  })
}

export const removeStyles = (node: HTMLElement, styleNames: string[]) => {
  each(styleNames, (k) => {
    node.style.removeProperty(k)
  })
}

///////////////////////////////////////////////////////////////////////////////

export const getCreditCardNumberMask = (value: string, flow: PaymentFlow) => {
  let mask = '0000 0000 0000 0000[ 000]'
  let cardNumber = (value || '').replace(/\s/g, '')
  let type = getCreditCardType(cardNumber, flow)

  if (type === PaymentMethod.AMERICAN_EXPRESS) {
    mask = '0000 000000 00000'
  } else if (type === PaymentMethod.VISA) {
  } else if (type === PaymentMethod.MASTER_CARD) {
    mask = '0000 0000 0000 0000'
  } else if (type === PaymentMethod.DISCOVERY) {
  } else if (type === PaymentMethod.DINERS_CLUB) {
  } else if (type === PaymentMethod.JAPANESE_CREDIT_BUREAU) {
  } else if (type === PaymentMethod.MAESTRO) {
  }

  return mask
}

///////////////////////////////////////////////////////////////////////////////

export const countryCodeCH = '+41'

export const omitCountryCode = (v) =>
  v.replace(countryCodeCH, '').replace(/\s/g, '')

export const getMsisdnMaskProps = (setValue: (v: string) => void) => ({
  mask: '00 000 00 00',
  onAccept: (value) => {
    let v = value.replace(/\s/g, '')

    if (!!v) {
      v = countryCodeCH + v
    }

    setValue(v)
  },
})

///////////////////////////////////////////////////////////////////////////////

export function markFieldAsDirty(dirtyFields: string[], field: string) {
  if (dirtyFields && dirtyFields.indexOf(field) === -1) {
    dirtyFields.push(field)
  }
}

export function markFieldAsNotDirty(dirtyFields: string[], field: string) {
  if (dirtyFields) {
    let idx = dirtyFields.indexOf(field)

    if (idx !== -1) {
      dirtyFields.splice(idx, 1)
    }
  }
}

///////////////////////////////////////////////////////////////////////////////

export function isFieldRequired(form: IFormBase<any>, name: string | string[]) {
  if (!form || !name) {
    return false
  }

  let fields = flatten([name])

  return intersection(form.requiredFields, fields).length > 0
}

export function isFieldInvalid(form: IFormBase<any>, name: string | string[]) {
  if (!form || !name) {
    return false
  }

  let fields = flatten([name])

  return intersection(keys(form.validationErrors), fields).length > 0
}

///////////////////////////////////////////////////////////////////////////////

export function getAmountSubunitFactor(
  currency: PaymentCurrency,
  config: WidgetConfig,
) {
  currency = (isString(currency)
    ? currency.toLocaleLowerCase()
    : undefined) as PaymentCurrency

  let amountSubunitFactor = config.amountSubunits[currency]

  if (amountSubunitFactor === undefined) {
    // fallback to 100 since most of the currencies has 100 subunits
    amountSubunitFactor = 100
  }

  return amountSubunitFactor
}

export function unitsToSubunits(
  amount: number,
  currency: PaymentCurrency,
  config: WidgetConfig,
): number {
  if (amount === null || currency === null) {
    return null
  }

  const amountSubunitFactor = getAmountSubunitFactor(currency, config)

  return Math.round(amount * amountSubunitFactor)
}

export function subunitsToUnits(
  amount: number,
  currency: PaymentCurrency,
  config: WidgetConfig,
): number {
  if (amount === null || currency === null) {
    return null
  }

  const amountSubunitFactor = getAmountSubunitFactor(currency, config)

  return amount / amountSubunitFactor
}

export function getAmountFloatDigitsCount(
  currency: PaymentCurrency,
  config: WidgetConfig,
): number {
  const amountSubunitFactor = getAmountSubunitFactor(currency, config)

  // get count of possible digits after floating point
  // depending of subunit factor for given currency
  // 100 -> 2, 1000 -> 3, 1 -> 0
  return Math.round(Math.log(amountSubunitFactor) / Math.log(10))
}

export function getCustomAmountInputRegExp(
  currency: PaymentCurrency,
  config: WidgetConfig,
): RegExp {
  const floatDigitsCount = getAmountFloatDigitsCount(currency, config)

  // tip: use https://regexper.com/ to visualize regexps
  if (floatDigitsCount > 0) {
    // decimal amount is allowed for given currency
    return new RegExp(`\\d*[.,]?\\d{0,${floatDigitsCount}}`, 'g')
  } else {
    // decimal amount is not allowed for given currency
    return new RegExp('\\d*', 'g')
  }
}

export function getCustomAmountInputStrictRegExp(
  currency: PaymentCurrency,
  config: WidgetConfig,
): RegExp {
  const floatDigitsCount = getAmountFloatDigitsCount(currency, config)

  // tip: use https://regexper.com/ to visualize regexps
  if (floatDigitsCount > 0) {
    // decimal amount is allowed for given currency
    return new RegExp(
      `^(?:(?:(?:[1-9]|$)[0-9]*)|0)(?:[.,]|$)\\d{0,${floatDigitsCount}}$`,
    )
  } else {
    // decimal amount is not allowed for given currency
    return new RegExp('^(?:[1-9]|$)[0-9]*$')
  }
}

export function parseAmount(
  rawValue: string | number | null,
  currency: PaymentCurrency,
  config: WidgetConfig,
): number | null {
  const customAmountInputRegExp = getCustomAmountInputRegExp(currency, config)

  if (isNumber(rawValue)) {
    rawValue = '' + rawValue
  }

  let value = (rawValue || '').toString().replace(/\s/g, '')

  const result = value.match(customAmountInputRegExp).filter((v) => !!v)

  if (result.length > 0) {
    value = result[0]
  }

  value = value.replace(',', '.')
  let amount = parseFloat(value)
  amount = isNaN(amount) || amount === 0 ? null : amount

  return amount
}

export const formatAmount = (
  rawValue: string | number,
  currency: PaymentCurrency,
  config: WidgetConfig,
): string => {
  let value: number

  try {
    value = parseFloat(rawValue as string)

    if (isNaN(value)) {
      return null
    }

    if (!isInteger(value)) {
      const floatDigitsCount = getAmountFloatDigitsCount(currency, config)

      return value.toFixed(floatDigitsCount)
    }

    return value.toString()
  } catch (e) {
    return null
  }
}

// Add suffix '.—' only if amount is integer and float digits count for current currency is more then zero
export const formatAmountWithSuffix = (
  rawAmount: string | number,
  currency: PaymentCurrency,
  config: WidgetConfig,
): string => {
  const amount = formatAmount(rawAmount, currency, config)
  const floatDigitsCount = getAmountFloatDigitsCount(currency, config)
  const addSuffix = isInteger(parseFloat(amount)) && floatDigitsCount > 0

  return addSuffix ? `${amount}.—` : amount
}

///////////////////////////////////////////////////////////////////////////////

export function translateEpikErrorMessages(
  validationErrors: ValidationErrors,
  trans: any,
): ValidationErrors {
  let errors = cloneDeep(validationErrors)

  each(errors, (fieldErrors) => {
    each(fieldErrors, (error) => {
      let message = trans(`epik_errors.${error.code}`, {defaultValue: null})

      if (!message) {
        message = error.message
      }

      error.message = message
    })
  })

  return errors
}

export function translateLocalErrorMessages(
  validationErrors: ValidationErrors,
  trans: any,
  scope: string,
): ValidationErrors {
  let errors = cloneDeep(validationErrors)

  each(errors, (fieldErrors, field) => {
    each(fieldErrors, (error) => {
      let message = trans(`${scope}.${field}.${error.code}`, {
        ...error.data,
        defaultValue: null,
      })

      if (!message) {
        message = error.message
      }

      error.message = message
    })
  })

  return errors
}

///////////////////////////////////////////////////////////////////////////////

export function getOrSet<T>(
  object: any,
  key: string | string[],
  defaultValue: T,
): T {
  if (get(object, key) === undefined) {
    set(object, key, defaultValue)
  }

  return get(object, key)
}

///////////////////////////////////////////////////////////////////////////////

export const prepareWidgetLocation = (router: Router): WidgetLocation => {
  const {pathname, search, hash} = router.getLocation()
  const params = getLocationParams(router)

  return {pathname, search, hash, params}
}

///////////////////////////////////////////////////////////////////////////////

// put priority options to the top of the list,
// sort other options by label
export const sortOptions = (
  options: OptionItem[],
  priorityList: string[],
): OptionItem[] => {
  const compare = (a: OptionItem, b: OptionItem) => {
    const av = a.value
    const bv = b.value

    const al = a.label
    const bl = b.label

    const avIdx = priorityList.indexOf(av as string)
    const bvIdx = priorityList.indexOf(bv as string)

    const aIsPrio = avIdx !== -1
    const bIsPrio = bvIdx !== -1

    if (aIsPrio && !bIsPrio) {
      return -1
    }

    if (!aIsPrio && bIsPrio) {
      return 1
    }

    if (aIsPrio && bIsPrio) {
      if (avIdx < bvIdx) {
        return -1
      }

      if (avIdx > bvIdx) {
        return 1
      }

      return 0
    }

    return al.localeCompare(bl)
  }

  return [...options].sort(compare)
}
